// 錄影政策的「設備錄影模式」列（vms-web §5.19.1「通道的錄影方式」）。
//
// 攝影機（source = sd）：由 kinio_recording.sd_enabled + sd_mode 推導（sd_enabled = false → off，否則 sd_mode），
// 值 continuous | event | off（SPEC #vms-web recording-policy）。
// NVR / Kinio Station 的通道（source = schedule）：錄影方式是兩張週排程，由 façade 推導。
// 雲端錄影全天且生效時，設備本機也全天錄（ADR-003 §2.5.1 取聯集）：façade 的 reported = continuous、
// mode_source = cloud_policy，週排程（使用者自己的值）在 desired / schedule_mode。選單顯示並寫回的是週排程，
// 畫面另外說明「雲端連續錄影中，本機也全天錄」。
import { dateStamp } from '../../lib/timefmt';
import { vms } from '@kinio/web-sdk';
import type { VmsDevice } from '../../lib/devices';
import type { ConfirmOptions } from '../../components/ConfirmDialog';

export interface PolicyView {
  device_mode?: DeviceModeView;
  cloud?: CloudView;
  cloud_retention?: { retention_days: number; source: string };
}

/** 只有攝影機本體的 SD 政策互斥；通道排程與雲端帶出的本機模式照舊。 */
export function exclusiveRecording(dev: Pick<VmsDevice, 'isCamera' | 'parentAid'>, p: PolicyView): boolean {
  return dev.isCamera && !dev.parentAid && p.device_mode?.source === 'sd' && p.device_mode.mode_source !== 'cloud_policy';
}

export function recordingTargetConfirm(
  t: (k: string) => string,
  dev: Pick<VmsDevice, 'isCamera' | 'parentAid'>,
  p: PolicyView,
  change: { deviceMode?: string; cloudEnabled?: boolean },
): ConfirmOptions | null {
  if (!exclusiveRecording(dev, p)) return null;
  const mode = deviceModeUi(p.device_mode!).value;
  const sdOn = mode === 'continuous' || mode === 'event';
  const cloudOn = !!p.cloud && p.cloud.cloud_enabled !== false;
  const target = change.cloudEnabled === true && !cloudOn && sdOn ? 'cloud'
    : mode === 'off' && (change.deviceMode === 'continuous' || change.deviceMode === 'event') && cloudOn ? 'sd' : null;
  if (!target) return null;
  return {
    title: t(`rpol.enable_${target}`),
    body: t(`rpol.confirm_${target}`) + (target === 'sd' && p.cloud?.seat ? t('rpol.confirm_sd_seat') : ''),
    action: t(`rpol.enable_${target}`),
  };
}

/** 通用設定與批次套設定也能開 SD：寫入前讀政策，席次只認 policy.cloud.seat。取消不寫任何值。 */
export async function confirmSdWrite(
  dev: VmsDevice,
  state: Record<string, Record<string, unknown>>,
  ask: (o: ConfirmOptions) => Promise<boolean>,
  t: (k: string) => string,
): Promise<boolean> {
  if (!dev.isCamera || dev.parentAid || state.kinio_recording?.sd_enabled !== true) return true;
  const p = await vms.recordingPolicy(dev.apiEndpoint, dev.homeId, dev.aid) as PolicyView;
  const confirm = recordingTargetConfirm(t, dev, p, { deviceMode: 'event' });
  return !confirm || ask(confirm);
}

export interface DeviceModeView {
  desired: string | null;
  reported: string | null;
  status: string;
  reason?: string | null;
  version: number;
  /** sd = 攝影機的 SD 錄影開關 + 模式；schedule = NVR / Station 通道的週排程 */
  source?: 'sd' | 'schedule' | string;
  mode_source?: 'schedule' | 'cloud_policy' | string;
  schedule_mode?: string | null;
}

export interface DeviceModeUi {
  /** 選單目前的值：使用者自己的設定（cloud_policy 時是週排程，不是雲端帶出的 continuous） */
  value: string;
  options: string[];
  /** 唯讀顯示：設備實際的錄影方式 */
  shown: string | null;
  /** 顯示「雲端連續錄影中，本機也全天錄；關掉雲錄後回到此排程」 */
  cloudPolicy: boolean;
}

export function deviceModeUi(dm: DeviceModeView): DeviceModeUi {
  const cloudPolicy = dm.mode_source === 'cloud_policy';
  const own = cloudPolicy ? (dm.schedule_mode ?? null) : dm.reported;
  return {
    value: dm.desired ?? own ?? '',
    options: dm.source === 'schedule' ? ['continuous', 'event', 'scheduled', 'off'] : ['continuous', 'event', 'off'],
    shown: dm.reported,
    cloudPolicy,
  };
}

// 雲端錄影模式（vms-web §5.9 / §5.19.1）：沒選過 = 事件（Eddy 2026-09-28）；全時錄影功能尚未開放時
// façade 帶 continuous_available = false，「全時」灰掉並顯示「全時錄影即將開放」（PUT 會回 422 FEATURE_DISABLED）。
export interface CloudView {
  cloud_enabled?: boolean | null;
  cloud_mode?: string | null;
  effective?: boolean;
  effective_mode?: string | null;
  reason?: string | null;
  continuous_available: boolean;
  seat?: { seat_id: string; mode: string } | null;
  /** 雲錄的生效來源（seat / home_plan / grant / exempt；沒生效 = null）、來源名稱（席次所屬的組織或家戶、方案名）、到期（B-09，platform vms_facade.cloudSource） */
  source?: 'seat' | 'home_plan' | 'grant' | 'exempt' | string | null;
  source_label?: string | null;
  source_until?: string | null;
}

/**
 * 雲端錄影列的「由誰提供」（SPEC #vms-web-327「雲端錄影的生效來源」；VMS UX 評審 2026-10-13 B-09）：
 * 跟方案與帳單的席次對得起來；沒有生效時給〔指派席次〕連 /billing#seats。舊後端沒帶 source → null（不推測）。
 */
export function cloudSourceText(
  t: (k: string, o?: Record<string, unknown>) => string,
  c: Pick<CloudView, 'effective' | 'source' | 'source_label' | 'source_until'>,
  _lang: string,
): { text: string; assign: boolean } | null {
  if (c.source === undefined) return null;
  const until = c.source_until ? dateStamp(c.source_until) : '';
  const label = c.source_label || '';
  switch (c.source) {
    case 'seat':
      return { text: label ? t('rpol.src_seat', { name: label }) : t('rpol.src_seat_plain'), assign: false };
    case 'home_plan':
      return { text: (label ? t('rpol.src_home_plan', { plan: label }) : t('rpol.src_home_plan_plain')) + (until ? t('rpol.src_until', { date: until }) : ''), assign: false };
    case 'grant':
      return { text: (label ? t('rpol.src_grant', { plan: label }) : t('rpol.src_grant_plain')) + (until ? t('rpol.src_until', { date: until }) : ''), assign: false };
    case 'exempt':
      return { text: t('rpol.src_exempt'), assign: false };
    default:
      return c.effective ? null : { text: t('rpol.src_none'), assign: true };
  }
}

/**
 * 雲端錄影列的狀態字（B-09）：開著寫「生效中（事件錄影）」（模式用譯名，不露 event / continuous）；
 * 開發環境豁免（source = exempt）不寫成單純的「生效中」，直接寫「未套用方案（開發環境豁免）」——席次 0 時看得出為什麼還在錄。
 */
export function cloudStateText(t: (k: string, o?: Record<string, unknown>) => string, c: Pick<CloudView, 'effective' | 'effective_mode' | 'source'>): string {
  if (!c.effective) return t('rpol.cloud_off');
  const mode = c.effective_mode ? t('rpol.mode_' + c.effective_mode, { defaultValue: t('rpol.mode_unknown') }) : '';
  if (c.source === 'exempt') return mode ? t('rpol.cloud_on_exempt', { mode }) : t('rpol.src_exempt');
  return mode ? t('rpol.cloud_on', { mode }) : t('rpol.cloud_on_plain');
}

/** 這台的機種不支援雲端錄影（沒有 kinio_recording；platform recpolicy.ReasonNotSupported，SPEC #vms-web-562）。 */
export function cloudUnsupported(c: Pick<CloudView, 'reason'> | undefined | null): boolean {
  return c?.reason === 'not_supported';
}

/** 雲錄沒生效的原因（platform recpolicy.Reason*）一律寫白話；沒有對照的走通用句，不露出原文（第四輪 B4-20）。 */
export const CLOUD_REASONS = ['not_supported', 'plan_no_cloud', 'home_disabled', 'disabled', 'mode_not_in_plan', 'camera_limit', 'continuous_paused'] as const;

export function cloudReasonText(t: (k: string, o?: Record<string, unknown>) => string, reason: string): string {
  return (CLOUD_REASONS as readonly string[]).includes(reason) ? t('rpol.reason_' + reason) : t('rpol.reason_other');
}

export interface CloudModeUi {
  value: string;
  options: { mode: 'event' | 'continuous'; disabled: boolean }[];
  /** 顯示「全時錄影即將開放」 */
  comingSoon: boolean;
}

export function cloudModeUi(c: CloudView): CloudModeUi {
  const available = c.continuous_available;
  return {
    value: c.cloud_mode === 'continuous' ? 'continuous' : 'event',
    options: [
      { mode: 'event', disabled: false },
      { mode: 'continuous', disabled: !available },
    ],
    comingSoon: !available,
  };
}

// 雲端錄影「設備尚未套用」（STN-02，SPEC #vms-web-s5-19-1「雲端錄影以平台為準」，Eddy 2026-10-11 選 desired / reported 收斂）：
// 設備把 ingest 下發的雲端錄影設定原值報回 shadow 唯讀 kinio_recording.cloud_applied {cloud_mode: none|event|continuous, retention_days, changed_at?}
// （NVR / Station 每路報在通道自己的 shadow，攝影機報本體）。比對 façade 的 cloud.effective / effective_mode：
// effective = false ↔ none、effective = true ↔ cloud_mode = effective_mode；不一樣 = 設備尚未套用；沒報 = 舊韌體，不知道（不推測）。
// retention_days 只做資訊顯示、不算未套用。
export interface CloudApplied {
  cloudMode: 'none' | 'event' | 'continuous';
  /** 0 = 沒給 */
  retentionDays: number;
  changedAt?: number;
}

export function cloudAppliedOf(reported: Record<string, unknown> | undefined): CloudApplied | null {
  const r = ((reported?.kinio_recording ?? null) as { cloud_applied?: unknown } | null)?.cloud_applied;
  if (!r || typeof r !== 'object') return null;
  const o = r as Record<string, unknown>;
  const m = o.cloud_mode;
  if (m !== 'none' && m !== 'event' && m !== 'continuous') return null;
  const days = typeof o.retention_days === 'number' && Number.isFinite(o.retention_days) ? o.retention_days : 0;
  const at = typeof o.changed_at === 'number' && Number.isFinite(o.changed_at) ? o.changed_at : undefined;
  return { cloudMode: m, retentionDays: days, ...(at !== undefined ? { changedAt: at } : {}) };
}

export type CloudApplyStatus = 'applied' | 'not_applied' | 'unknown';

export function cloudApplyStatus(cloud: Pick<CloudView, 'effective' | 'effective_mode'>, applied: CloudApplied | null): CloudApplyStatus {
  if (!applied) return 'unknown';
  const want = cloud.effective ? cloud.effective_mode ?? '' : 'none';
  return applied.cloudMode === want ? 'applied' : 'not_applied';
}
